import React, { useEffect, useState } from "react";
import { X, Sparkles, Loader2, Calendar, CheckCircle2, AlertTriangle, ShieldCheck, Target, Trash2, Pencil, Save } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePanel } from "@/context/PanelContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatTime, formatHM } from "@/lib/utils-tg";
import Thread from "@/components/Thread";
import EditTaskModal from "@/components/EditTaskModal";
import ParticipantPicker from "@/components/ParticipantPicker";
import ParticipantBubbles from "@/components/ParticipantBubbles";
import { useConfirm } from "@/components/ConfirmDialog";

const TYPE_META = {
  task: { icon: CheckCircle2, color: "text-blue-600", bg: "bg-blue-50", label: "Task" },
  meeting: { icon: Calendar, color: "text-emerald-600", bg: "bg-emerald-50", label: "Meeting" },
  issue: { icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50", label: "Issue" },
  approval: { icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-50", label: "Approval" },
  goal: { icon: Target, color: "text-emerald-600", bg: "bg-emerald-50", label: "Goal" },
};

export default function RightPanel() {
  const { panel, close, triggerRefresh } = usePanel();
  if (!panel) return null;
  const meta = TYPE_META[panel.type] || TYPE_META.task;

  return (
    <aside
      className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white border-l border-slate-200 z-40 flex flex-col shadow-xl animate-slide-in-right"
      data-testid="right-panel"
    >
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <div className={`tg-stat-icon ${meta.bg}`}>
            <meta.icon className={`h-5 w-5 ${meta.color}`} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{meta.label}</div>
            <div className="font-display font-bold text-base text-slate-900 line-clamp-1">{panel.data.title}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400"
          data-testid="panel-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Tabs defaultValue={panel.data?.__initialTab || (panel.type === "meeting" ? "summary" : "details")} className="flex-1 flex flex-col min-h-0 px-5">
        <TabsList className="bg-transparent p-0 h-auto justify-start gap-4 border-b border-slate-200 rounded-none w-full">
          {panel.type === "meeting" && (
            <TabsTrigger
              value="summary"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-emerald-600 data-[state=active]:border-emerald-600 border-b-2 border-transparent rounded-none px-1 py-2.5 text-sm font-semibold text-slate-500"
              data-testid="tab-summary"
            >
              Summary
            </TabsTrigger>
          )}
          <TabsTrigger
            value="details"
            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-emerald-600 data-[state=active]:border-emerald-600 border-b-2 border-transparent rounded-none px-1 py-2.5 text-sm font-semibold text-slate-500"
            data-testid="tab-details"
          >
            Details
          </TabsTrigger>
          {panel.type === "meeting" && (
            <TabsTrigger
              value="actions"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-emerald-600 data-[state=active]:border-emerald-600 border-b-2 border-transparent rounded-none px-1 py-2.5 text-sm font-semibold text-slate-500"
              data-testid="tab-actions"
            >
              Action items
            </TabsTrigger>
          )}
          <TabsTrigger
            value="thread"
            className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-emerald-600 data-[state=active]:border-emerald-600 border-b-2 border-transparent rounded-none px-1 py-2.5 text-sm font-semibold text-slate-500"
            data-testid="tab-thread"
          >
            Thread
          </TabsTrigger>
        </TabsList>

        {panel.type === "meeting" && (
          <TabsContent value="summary" className="flex-1 overflow-y-auto py-4 mt-0">
            <MeetingSummary meeting={panel.data} onUpdated={triggerRefresh} />
          </TabsContent>
        )}

        <TabsContent value="details" className="flex-1 overflow-y-auto py-4 mt-0">
          {panel.type === "task" && <TaskDetails task={panel.data} onUpdated={triggerRefresh} />}
          {panel.type === "meeting" && <MeetingDetails meeting={panel.data} onUpdated={triggerRefresh} />}
          {panel.type === "issue" && <IssueDetails issue={panel.data} onUpdated={triggerRefresh} />}
          {panel.type === "approval" && <ApprovalDetails approval={panel.data} onUpdated={triggerRefresh} />}
          {panel.type === "goal" && <GoalDetails goal={panel.data} onUpdated={triggerRefresh} />}
        </TabsContent>

        {panel.type === "meeting" && (
          <TabsContent value="actions" className="flex-1 overflow-y-auto py-4 mt-0">
            <MeetingActionItems meetingId={panel.id} onUpdated={triggerRefresh} />
          </TabsContent>
        )}

        <TabsContent value="thread" className="flex-1 min-h-0 mt-0 pb-2">
          <Thread objectType={panel.type} objectId={panel.id} />
        </TabsContent>
      </Tabs>
    </aside>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function TaskDetails({ task, onUpdated }) {
  const [status, setStatus] = useState(task.status);
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState(task.assignee_id || "");
  const { close } = usePanel();
  const { user: me } = useAuth();
  const confirm = useConfirm();

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const STATUSES = [
    { v: "todo", label: "To do" },
    { v: "doing", label: "In progress" },
    { v: "done", label: "Done" },
  ];
  const update = async (next) => {
    setStatus(next);
    try {
      await api.patch(`/tasks/${task.id}`, { status: next });
      toast.success("Task updated");
      onUpdated();
    } catch {
      toast.error("Failed to update");
    }
  };
  const del = async () => {
    const ok = await confirm({
      title: "Delete task?",
      description: "This can't be undone.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      onUpdated();
      close();
    } catch {
      toast.error("Delete failed");
    }
  };
  return (
    <div data-testid="task-details">
      <div className="flex justify-end -mt-2 mb-1">
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
          title="Edit task"
          data-testid="edit-task-button"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <Field label="Description">{task.description || <span className="text-slate-400">No description.</span>}</Field>
      <Field label="Status">
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              onClick={() => update(s.v)}
              className={`tg-pill ${
                status === s.v
                  ? s.v === "done"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : s.v === "doing"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-100 text-slate-700 border-slate-200"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`status-${s.v}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Due">{task.due_date || <span className="text-slate-400">—</span>}</Field>
      <Field label="Assignee">
        <select
          className="tg-input py-1.5 text-xs"
          value={assigneeId}
          onChange={async (e) => {
            const next = e.target.value;
            setAssigneeId(next);
            try {
              await api.patch(`/tasks/${task.id}`, { assignee_id: next });
              toast.success(next === me?.id ? "Assigned to you" : "Assigned to teammate");
              onUpdated();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Failed to assign");
            }
          }}
          data-testid="task-assignee-select"
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.id === me?.id ? `${u.name} (me)` : u.name} — {u.email}
            </option>
          ))}
        </select>
      </Field>
      <button
        onClick={del}
        className="mt-3 inline-flex items-center gap-2 text-sm text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-xl transition-colors"
        data-testid="delete-task-button"
      >
        <Trash2 className="h-4 w-4" /> Delete task
      </button>
      <EditTaskModal
        open={editing}
        onOpenChange={setEditing}
        task={task}
        onSaved={onUpdated}
      />
    </div>
  );
}

function MeetingSummary({ meeting, onUpdated }) {
  const [m, setM] = useState(meeting);
  const [busy, setBusy] = useState(false);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/extract-meeting", { meeting_id: m.id });
      setM({ ...m, ai_summary: data.summary, decisions: data.decisions });
      toast.success(`AI extracted ${data.tasks.length} tasks`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extract.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="meeting-summary">
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">AI Summary</div>
        </div>
        <div className="text-sm text-slate-700 leading-relaxed">
          {m.ai_summary || (
            <span className="text-slate-400">
              No summary yet. Paste your transcript or notes in the Details tab, then click below to extract a summary, decisions, and tasks.
            </span>
          )}
        </div>
      </div>

      {m.decisions && m.decisions.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Key Decisions</div>
          <ul className="space-y-2">
            {m.decisions.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button onClick={regenerate} disabled={busy} className="tg-btn-secondary w-full" data-testid="regenerate-summary">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-emerald-600" />}
        {busy ? "Generating…" : "Regenerate with AI"}
      </button>
    </div>
  );
}

function MeetingDetails({ meeting, onUpdated }) {
  const { close } = usePanel();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(!!meeting.__startEditing);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: meeting.title || "",
    starts_at: (meeting.starts_at || "").slice(0, 16), // YYYY-MM-DDTHH:mm
    duration_minutes: meeting.duration_minutes ?? 30,
    participants: (meeting.participants || []).join(", "),
    participant_ids: meeting.participant_ids || [],
    notes: meeting.notes || "",
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        duration_minutes: Number(form.duration_minutes) || 30,
        participants: form.participants
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        participant_ids: form.participant_ids,
        notes: form.notes,
      };
      await api.patch(`/meetings/${meeting.id}`, payload);
      toast.success("Meeting updated");
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete meeting?",
      description: "All notes and the transcript will be removed.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/meetings/${meeting.id}`);
      toast.success("Meeting deleted");
      onUpdated();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <div data-testid="meeting-edit">
        <Field label="Title">
          <input
            className="tg-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="edit-meeting-title"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Starts at">
            <input
              type="datetime-local"
              className="tg-input"
              value={form.starts_at}
              onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              data-testid="edit-meeting-starts-at"
            />
          </Field>
          <Field label="Duration (min)">
            <input
              type="number"
              min="1"
              className="tg-input"
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
              data-testid="edit-meeting-duration"
            />
          </Field>
        </div>
        <Field label="Participants (comma-separated)">
          <input
            className="tg-input"
            value={form.participants}
            onChange={(e) => setForm({ ...form, participants: e.target.value })}
            placeholder="alice@co.com, bob@co.com"
            data-testid="edit-meeting-participants"
          />
        </Field>
        <Field label="Tag teammates">
          <ParticipantPicker
            value={form.participant_ids}
            onChange={(ids) => setForm({ ...form, participant_ids: ids })}
            placeholder="Tag anyone from your team — they'll see this meeting & the thread"
          />
        </Field>
        <Field label="Notes / Transcript">
          <textarea
            className="tg-input min-h-[180px] resize-none"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Paste your meeting transcript or notes here. Then go to the Summary tab and tap Regenerate with AI to auto-extract a summary, decisions, and tasks."
            data-testid="edit-meeting-notes"
          />
        </Field>
        <div className="flex gap-2 mt-2">
          <button
            onClick={save}
            disabled={busy || !form.title.trim()}
            className="tg-btn-primary flex-1 disabled:opacity-50"
            data-testid="save-meeting"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="tg-btn-secondary flex-1"
            data-testid="cancel-edit-meeting"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="meeting-details">
      <div className="flex justify-end -mt-2 mb-1">
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
          title="Edit meeting"
          data-testid="edit-meeting-button"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <Field label="When">{formatTime(meeting.starts_at)} • {meeting.duration_minutes ?? 30}m</Field>
      <Field label="Participants">
        {(meeting.participants || []).join(", ") || <span className="text-slate-400">—</span>}
      </Field>
      {(meeting.participant_ids?.length > 0) && (
        <Field label="Tagged teammates">
          <ParticipantBubbles ids={meeting.participant_ids} />
        </Field>
      )}
      <Field label="Notes / Transcript">
        <div className="whitespace-pre-line text-slate-700 leading-relaxed">
          {meeting.notes || <span className="text-slate-400">No notes.</span>}
        </div>
      </Field>
      <button
        onClick={del}
        className="mt-3 inline-flex items-center gap-2 text-sm text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-xl transition-colors"
        data-testid="delete-meeting-button"
      >
        <Trash2 className="h-4 w-4" /> Delete meeting
      </button>
    </div>
  );
}

function IssueDetails({ issue, onUpdated }) {
  const { close } = usePanel();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: issue.title || "",
    description: issue.description || "",
    participant_ids: issue.participant_ids || [],
  });

  const resolve = async () => {
    try {
      await api.patch(`/issues/${issue.id}/resolve`);
      toast.success("Issue resolved");
      onUpdated();
    } catch {
      toast.error("Failed");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/issues/${issue.id}`, {
        title: form.title.trim(),
        description: form.description,
        participant_ids: form.participant_ids,
      });
      toast.success("Issue updated");
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete issue?",
      description: "The thread and all its messages will be removed.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/issues/${issue.id}`);
      toast.success("Issue deleted");
      onUpdated();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <div data-testid="issue-edit">
        <Field label="Title">
          <input
            className="tg-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="edit-issue-title"
          />
        </Field>
        <Field label="Description">
          <textarea
            className="tg-input min-h-[120px] resize-none"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What's blocking?"
            data-testid="edit-issue-description"
          />
        </Field>
        <Field label="Tag teammates">
          <ParticipantPicker
            value={form.participant_ids}
            onChange={(ids) => setForm({ ...form, participant_ids: ids })}
            placeholder="Loop in anyone who can help"
          />
        </Field>
        <div className="flex gap-2 mt-2">
          <button
            onClick={save}
            disabled={busy || !form.title.trim()}
            className="tg-btn-primary flex-1 disabled:opacity-50"
            data-testid="save-issue"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="tg-btn-secondary flex-1"
            data-testid="cancel-edit-issue"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="issue-details">
      <div className="flex justify-end -mt-2 mb-1">
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
          title="Edit issue"
          data-testid="edit-issue-button"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <Field label="Description">
        {issue.description || <span className="text-slate-400">No description.</span>}
      </Field>
      <Field label="Status">
        <span
          className={`tg-pill ${
            issue.status === "resolved"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-orange-50 text-orange-700 border-orange-200"
          }`}
        >
          {issue.status}
        </span>
      </Field>
      {(issue.participant_ids?.length > 0) && (
        <Field label="Tagged teammates">
          <ParticipantBubbles ids={issue.participant_ids} />
        </Field>
      )}
      {issue.status !== "resolved" && (
        <button onClick={resolve} className="tg-btn-primary w-full mt-2" data-testid="resolve-issue">
          <CheckCircle2 className="h-4 w-4" /> Mark resolved
        </button>
      )}
      <button
        onClick={del}
        className="mt-3 inline-flex items-center gap-2 text-sm text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-xl transition-colors"
        data-testid="delete-issue-button"
      >
        <Trash2 className="h-4 w-4" /> Delete issue
      </button>
    </div>
  );
}

function ApprovalDetails({ approval, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState(approval.status);
  const [decidedLocal, setDecidedLocal] = useState(null);
  const isTimesheet = approval.type === "timesheet" && approval.linked_submission_id;

  useEffect(() => {
    setDetail(null);
    if (isTimesheet) {
      api.get(`/timesheet/submissions/${approval.linked_submission_id}/detail`)
        .then((r) => setDetail(r.data))
        .catch(() => {});
    }
  }, [approval.id, approval.linked_submission_id, isTimesheet]);

  const decide = async (decision) => {
    try {
      if (isTimesheet) {
        await api.patch(`/timesheet/submissions/${approval.linked_submission_id}/supervisor`, { decision });
      } else {
        await api.patch(`/approvals/${approval.id}`, { decision });
      }
      setStatus(decision);
      setDecidedLocal(new Date().toISOString());
      toast.success(decision === "approved" ? "Approved" : "Rejected");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openPdf = () => {
    const sub = detail?.submission;
    if (!sub) return;
    window.open(`/timesheet/print/${sub.period_number}?sub=${sub.id}`, "_blank");
  };

  const decidedAt = decidedLocal || approval.decided_at || detail?.submission?.supervisor_approved_at || detail?.submission?.rejected_at;

  return (
    <div data-testid="approval-details">
      <Field label="Type">{approval.type}</Field>
      {approval.amount != null && <Field label="Amount">${Number(approval.amount).toFixed(2)}</Field>}
      <Field label="Description">
        {approval.description || <span className="text-slate-400">—</span>}
      </Field>

      {isTimesheet && (
        <div className="mt-1 mb-2" data-testid="approval-timesheet-breakdown">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Hours logged</div>
            <button onClick={openPdf} disabled={!detail} className="text-xs text-emerald-700 font-semibold hover:underline disabled:opacity-40" data-testid="approval-open-pdf">
              Open PDF →
            </button>
          </div>
          {!detail ? (
            <div className="text-xs text-slate-400 py-2">Loading entries…</div>
          ) : detail.entries.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">No entries.</div>
          ) : (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-60 overflow-auto">
              {detail.entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid="approval-entry-row">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{e.title || e.description}</div>
                    <div className="text-slate-400">{e.date}{e.start_time ? ` · ${e.start_time}–${e.end_time || ""}` : ""}</div>
                  </div>
                  <div className="font-semibold text-slate-700 whitespace-nowrap pl-2">{formatHM(e.duration_minutes || 0)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2 text-xs bg-emerald-50 font-bold text-emerald-800">
                <span>Total</span>
                <span>{formatHM(detail.total_minutes)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <Field label="Status">
        <span
          className={`tg-pill ${
            status === "approved"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : status === "rejected"
              ? "bg-rose-50 text-rose-700 border-rose-200"
              : "bg-orange-50 text-orange-700 border-orange-200"
          }`}
        >
          {status}
        </span>
      </Field>
      {status !== "pending" && decidedAt && (
        <Field label="Decided">
          <span className="text-slate-600" data-testid="approval-decided-at">{decidedAt.replace("T", " ").slice(0, 16)}</span>
        </Field>
      )}
      {status === "pending" && (
        <div className="flex gap-2 mt-2">
          <button onClick={() => decide("approved")} className="tg-btn-primary flex-1" data-testid="approve-button">
            Approve
          </button>
          <button onClick={() => decide("rejected")} className="tg-btn-secondary flex-1" data-testid="reject-button">
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function GoalDetails({ goal, onUpdated }) {
  const { close } = usePanel();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: goal.title || "",
    description: goal.description || "",
    sprint_start: goal.sprint_start || "",
    sprint_end: goal.sprint_end || "",
    participant_ids: goal.participant_ids || [],
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/goals/${goal.id}`, {
        title: form.title.trim(),
        description: form.description,
        sprint_start: form.sprint_start || null,
        sprint_end: form.sprint_end || null,
        participant_ids: form.participant_ids,
      });
      toast.success("Goal updated");
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete goal?",
      description: "Linked tasks won't be removed — they'll just stop counting toward this goal.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/goals/${goal.id}`);
      toast.success("Goal deleted");
      onUpdated?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <div data-testid="goal-edit">
        <Field label="Title">
          <input
            className="tg-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="edit-goal-title"
          />
        </Field>
        <Field label="Description">
          <textarea
            className="tg-input min-h-[100px] resize-none"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="edit-goal-description"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Sprint start">
            <input
              type="date"
              className="tg-input"
              value={form.sprint_start}
              onChange={(e) => setForm({ ...form, sprint_start: e.target.value })}
              data-testid="edit-goal-sprint-start"
            />
          </Field>
          <Field label="Sprint end">
            <input
              type="date"
              className="tg-input"
              value={form.sprint_end}
              onChange={(e) => setForm({ ...form, sprint_end: e.target.value })}
              data-testid="edit-goal-sprint-end"
            />
          </Field>
        </div>
        <Field label="Tag teammates">
          <ParticipantPicker
            value={form.participant_ids}
            onChange={(ids) => setForm({ ...form, participant_ids: ids })}
            placeholder="Collaborators on this goal"
          />
        </Field>
        <div className="flex gap-2 mt-2">
          <button
            onClick={save}
            disabled={busy || !form.title.trim()}
            className="tg-btn-primary flex-1 disabled:opacity-50"
            data-testid="save-goal"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="tg-btn-secondary flex-1"
            data-testid="cancel-edit-goal"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="goal-details">
      <div className="flex justify-end -mt-2 mb-1">
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
          title="Edit goal"
          data-testid="edit-goal-button"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <Field label="Description">
        {goal.description || <span className="text-slate-400">No description.</span>}
      </Field>
      <Field label="Sprint">
        {goal.sprint_start} → {goal.sprint_end}
      </Field>
      <Field label="Progress">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${goal.progress || 0}%` }}
          />
        </div>
        <div className="text-xs text-slate-500 mt-1">{goal.progress || 0}% complete</div>
      </Field>
      {(goal.participant_ids?.length > 0) && (
        <Field label="Tagged teammates">
          <ParticipantBubbles ids={goal.participant_ids} />
        </Field>
      )}
      <button
        onClick={del}
        className="mt-3 inline-flex items-center gap-2 text-sm text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-xl transition-colors"
        data-testid="delete-goal-button"
      >
        <Trash2 className="h-4 w-4" /> Delete goal
      </button>
    </div>
  );
}


function MeetingActionItems({ meetingId, onUpdated }) {
  const confirm = useConfirm();
  const { refreshKey } = usePanel();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get(`/meetings/${meetingId}`);
    setTasks(r.data?.tasks || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, refreshKey]);

  const STATUS_PILL = {
    todo: "bg-slate-100 text-slate-700 border-slate-200",
    doing: "bg-emerald-50 text-emerald-700 border-emerald-200",
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const STATUS_LABEL = { todo: "To do", doing: "In progress", done: "Done" };

  const remove = async (task) => {
    const ok = await confirm({
      title: "Delete action item?",
      description: `"${task.title}" will be permanently removed.`,
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Action item deleted");
      load();
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>;
  }
  if (!tasks.length) {
    return (
      <div className="text-sm text-slate-400 py-8 text-center" data-testid="actions-empty">
        No action items yet. Go to the <span className="font-semibold">Summary</span> tab and click <span className="font-semibold">Regenerate with AI</span> to extract them from your transcript.
      </div>
    );
  }

  return (
    <div data-testid="meeting-actions">
      <div className="text-[11px] text-slate-500 mb-3">
        AI-extracted action items from this meeting. Edit or delete each below; manage them in your To dos.
      </div>
      <div className="space-y-2">
        {tasks.map((t) => (
          <div
            key={t.id}
            className="group rounded-xl border border-slate-200 bg-white p-3"
            data-testid={`action-item-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold text-slate-800 flex-1">{t.title}</div>
              <span className={`tg-pill text-[10px] ${STATUS_PILL[t.status] || STATUS_PILL.todo}`}>
                {STATUS_LABEL[t.status] || "To do"}
              </span>
            </div>
            {t.description && (
              <div className="text-xs text-slate-500 mt-1">{t.description}</div>
            )}
            <div className="flex items-center justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setEditing(t)}
                className="h-7 w-7 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 flex items-center justify-center"
                title="Edit action item"
                data-testid={`action-edit-${t.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => remove(t)}
                className="h-7 w-7 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
                title="Delete action item"
                data-testid={`action-delete-${t.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <EditTaskModal
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        task={editing}
        onSaved={() => { load(); onUpdated(); }}
      />
    </div>
  );
}
